/**
 * Impersonation store for admin user impersonation
 * Persists to localStorage for page reload survival
 */

import type { ImpersonatedUser } from "@otalytics/shared";

const STORAGE_KEY = "impersonation";

class ImpersonationStore {
  private impersonatedUser: ImpersonatedUser | null = null;
  private initialized = false;

  /**
   * Initialize store from localStorage (call on app start)
   */
  init(): void {
    if (this.initialized || typeof window === "undefined") return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.impersonatedUser = JSON.parse(stored);
      }
    } catch {
      // Invalid stored data, clear it
      localStorage.removeItem(STORAGE_KEY);
    }

    this.initialized = true;
  }

  /**
   * Set the impersonated user
   */
  setImpersonatedUser(user: ImpersonatedUser | null): void {
    this.impersonatedUser = user;

    if (typeof window === "undefined") return;

    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Get the impersonated user
   */
  getImpersonatedUser(): ImpersonatedUser | null {
    return this.impersonatedUser;
  }

  /**
   * Get the impersonated user's database ID (for API header)
   */
  getUserId(): string | null {
    return this.impersonatedUser?.id ?? null;
  }

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return this.impersonatedUser !== null;
  }

  /**
   * Clear impersonation
   */
  clear(): void {
    this.setImpersonatedUser(null);
  }
}

export const impersonationStore = new ImpersonationStore();
